/**
 * LayerPanel — Globe takeover layer toggle rail (S3.2).
 *
 * Renders the flat 10-toggle set declared in `useGlobeLayerState`
 * (PRD FR-5.5 + cleanup plan §3 amendment 2026-05-26). No section
 * hierarchy: one toggle per row, hue + label + checkbox.
 */

import { cn } from '@/lib/utils';
import {
  GLOBE_LAYER_DEFS,
  type GlobeLayerKey,
  type GlobeLayerState,
} from '../../hooks/useGlobeLayerState';

interface LayerPanelProps {
  state: GlobeLayerState;
  onToggle: (key: GlobeLayerKey) => void;
  /** Optional per-toggle count badge (e.g., visible-signal count). */
  counts?: Partial<Record<GlobeLayerKey, number>>;
  /** Optional className passthrough for positioning by the parent. */
  className?: string;
}

interface ToggleRowProps {
  layerKey: GlobeLayerKey;
  label: string;
  color: string;
  checked: boolean;
  count?: number;
  onToggle: () => void;
}

function ToggleRow({ layerKey, label, color, checked, count, onToggle }: ToggleRowProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={checked}
      data-layer-key={layerKey}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors rounded',
        'hover:bg-[var(--e-bg-hover)] cursor-pointer',
      )}
    >
      <span
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: checked ? color : 'var(--e-text-muted)' }}
      />
      <span
        className={cn(
          'w-7 h-4 rounded-full flex-shrink-0 relative transition-colors',
          checked ? 'bg-[var(--e-accent)]' : 'bg-[var(--e-text-muted)]/20',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform',
            checked ? 'translate-x-3.5' : 'translate-x-0.5',
          )}
        />
      </span>
      <span className="text-[11px] text-[var(--e-text-primary)] flex-1 truncate">
        {label}
      </span>
      {count !== undefined && (
        <span className="text-[9px] font-mono text-[var(--e-text-muted)]">
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}

export function LayerPanel({ state, onToggle, counts, className }: LayerPanelProps) {
  const visibleCount = Object.values(state).filter(Boolean).length;

  return (
    <div
      data-testid="layer-panel"
      className={cn(
        'w-56 rounded-lg border border-[var(--e-border-secondary)]',
        'bg-[var(--e-bg-card)]/95 backdrop-blur-md shadow-lg overflow-hidden',
        className,
      )}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--e-border-secondary)]/50">
        <span className="text-[10px] font-semibold tracking-wider uppercase text-[var(--e-text-muted)]">
          Layers
        </span>
        <span className="text-[9px] font-mono text-[var(--e-text-muted)]">
          {visibleCount}/{GLOBE_LAYER_DEFS.length}
        </span>
      </div>
      <div className="py-1">
        {GLOBE_LAYER_DEFS.map((def) => (
          <ToggleRow
            key={def.id}
            layerKey={def.id}
            label={def.label}
            color={def.color}
            checked={state[def.id]}
            count={counts?.[def.id]}
            onToggle={() => onToggle(def.id)}
          />
        ))}
      </div>
    </div>
  );
}
